import { useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../shared/api';
import { useDebouncedValue } from '../../shared/useDebouncedValue';
import { Icon } from '../../ui/Icon';

/** Max rows fetched per search. We never bulk-pull the whole EMR patient
 * table — the search is server-side, so this scales regardless of table size.
 * See docs/EMR_PATIENT_SEARCH.md. */
const PAGE_LIMIT = 500;

export function AllPatientsPage() {
  const gridRef = useRef<AgGridReact | null>(null);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  // Server-side search: empty query returns the first page by name; typing
  // (debounced) re-queries the full EMR table and returns up to PAGE_LIMIT.
  const debounced = useDebouncedValue(search.trim(), 300);
  const { data: patients, isFetching } = useQuery({
    queryKey: ['emr-patients', debounced],
    queryFn: () =>
      api.get('/patients', { params: { q: debounced, limit: PAGE_LIMIT } }).then((r) => r.data),
    placeholderData: (prev) => prev,
  });
  const rows = patients ?? [];
  const atLimit = rows.length >= PAGE_LIMIT;

  function startIntake(patient: any) {
    navigate('/intake', {
      state: {
        fromCandidate: {
          firstName: patient.firstName ?? '',
          lastName: patient.lastName ?? '',
          externalRef: patient.id != null ? String(patient.id) : undefined,
        },
      },
    });
  }

  const columnDefs = useMemo<ColDef[]>(
    () => [
      { headerName: 'ID', field: 'id', width: 90, pinned: 'left' },
      { headerName: 'First name', field: 'firstName', width: 150, pinned: 'left' },
      { headerName: 'Last name', field: 'lastName', width: 150, pinned: 'left' },
      {
        headerName: 'DOB',
        field: 'dob',
        width: 130,
        valueFormatter: (p) => (p.value ? String(p.value).slice(0, 10) : ''),
      },
      { headerName: 'Age', field: 'age', width: 80 },
      { headerName: 'Gender', field: 'gender', width: 110 },
      { headerName: 'Phone', field: 'phone', width: 150 },
      { headerName: 'Mobile', field: 'mobilePhone', width: 150 },
      { headerName: 'Email', field: 'email', width: 230 },
      { headerName: 'Address', field: 'address1', width: 200 },
      { headerName: 'City', field: 'city', width: 130 },
      { headerName: 'State', field: 'state', width: 80 },
      { headerName: 'Zip', field: 'zipCode', width: 100 },
      {
        headerName: 'Action',
        width: 120,
        pinned: 'right',
        sortable: false,
        filter: false,
        cellRenderer: (p: any) => (
          <button
            className="text-brand-700 hover:underline"
            onClick={() => startIntake(p.data)}
          >
            Intake
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex h-full flex-col gap-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">EMR Clients</h1>
          <p className="text-sm text-ink-subtle">
            Read-only view of all clients from the EMR database.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-ink-muted">
            {rows.length}
            {atLimit ? '+' : ''} shown{isFetching ? ' · searching…' : ''}
          </span>
          <button
            className="btn-secondary h-9 whitespace-nowrap"
            onClick={() => gridRef.current?.api.exportDataAsCsv({ fileName: 'patients.csv' })}
          >
            Export CSV
          </button>
        </div>
      </header>

      <div className="flex flex-col gap-1">
        <div className="relative w-full max-w-xs">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted">
            <Icon name="users" size={14} />
          </span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, patient ID, DOB, phone…"
            className="h-9 w-full rounded-md border border-line bg-white pl-9 pr-3 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
        </div>
        {atLimit && (
          <p className="text-2xs text-ink-muted">
            Showing the first {PAGE_LIMIT} matches — refine your search to narrow the list.
          </p>
        )}
      </div>

      <div className="ag-theme-quartz flex-1 overflow-hidden rounded-card shadow-card ring-1 ring-line">
        <AgGridReact
          ref={gridRef}
          rowData={rows}
          columnDefs={columnDefs}
          animateRows
          pagination
          paginationPageSize={100}
          paginationPageSizeSelector={[50, 100, 200, 500]}
          loading={isFetching}
          overlayLoadingTemplate='<span class="text-sm text-slate-500">Loading clients…</span>'
          overlayNoRowsTemplate="No clients found"
        />
      </div>
    </div>
  );
}
